import { useEffect, useMemo, useState } from 'react';
import {
  HeartPulse,
  Plus,
  Search,
  Stethoscope,
  Pill as PillIcon,
  ShieldCheck,
  Lock,
  Save,
  ChevronLeft,
  Calendar,
  AlertTriangle,
  Sparkles,
  Activity,
} from 'lucide-react';
import { Button, Card, Input, Modal, Pill, useToast } from '@nako/ui';
import { formatMoney } from '@nako/core';
import type {
  Patient,
  PatientVisit,
  PatientPrescription,
  PatientVitals,
  Gender,
  VisitType,
  PaymentStatus,
  PatientAuditAction,
} from '@nako/types';
import { isHealthcareBusiness } from '@nako/types';
import { useAuthStore } from '@/stores/auth';
import { useRegionStore, useRegionConfig } from '@/stores/region';
import { supabase } from '@/lib/supabase';
import { ToothIcon } from '@/components/dental/ToothIcon';
import { ToothChart } from '@/components/dental/ToothChart';
import { playChime } from '@/lib/chime';

const isDental = (industry: string | null) => !!industry && /dent/i.test(industry);

/**
 * Patient / Client management for healthcare practices.
 *
 * - Hidden unless the business industry is one of the Healthcare / *
 *   options. Customers in retail, transport, etc. never see this page.
 * - Every read and write of a patient record writes a row to
 *   patient_audit_log so the practice can answer DSAR requests and
 *   investigate breaches.
 * - Visit records become locked once the doctor closes them, after
 *   which they can be viewed but never edited (audit integrity).
 */
export default function Patients() {
  const business = useAuthStore((s) => s.business)!;
  const profile = useAuthStore((s) => s.profile);
  const isHealthcare = isHealthcareBusiness(business.industry);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isHealthcare) {
      setLoading(false);
      return;
    }
    void load();
  }, [business.id, isHealthcare]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('patients')
      .select('*')
      .eq('business_id', business.id)
      .is('deleted_at', null)
      .order('last_visit_at', { ascending: false, nullsFirst: false })
      .order('last_name');
    setPatients((data ?? []) as Patient[]);
    setLoading(false);
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) =>
      [p.first_name, p.last_name, p.national_id ?? '', p.phone ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [patients, search]);

  // Industry gate — render after all hooks have run.
  if (!isHealthcare) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-10 text-center">
        <Stethoscope size={32} className="text-muted mx-auto mb-3 opacity-50" />
        <h1 className="font-display mb-1 text-xl">Patients module</h1>
        <p className="text-muted text-sm">
          This module is available for healthcare practices (doctors, dentists, pharmacists,
          clinics). Update your industry in Settings to enable it.
        </p>
      </main>
    );
  }

  const audit = (
    action: PatientAuditAction,
    patientId: string | null,
    context: Record<string, unknown> = {},
  ) =>
    supabase.from('patient_audit_log').insert({
      business_id: business.id,
      patient_id: patientId,
      actor_id: profile?.id ?? null,
      action,
      context,
    });

  if (selectedId) {
    return (
      <PatientDetail
        patientId={selectedId}
        businessId={business.id}
        actorId={profile?.id ?? null}
        attendedByDefault={profile?.id ?? null}
        dental={isDental(business.industry)}
        onBack={() => {
          setSelectedId(null);
          void load();
        }}
      />
    );
  }

  const dental = isDental(business.industry);
  const totalConsented = patients.filter((p) => p.consent_to_store_records).length;
  const consentPct = patients.length > 0 ? Math.round((totalConsented / patients.length) * 100) : 0;
  const visitsThisMonth = patients.filter((p) => {
    if (!p.last_visit_at) return false;
    const d = new Date(p.last_visit_at);
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  }).length;

  return (
    <main className="mx-auto max-w-4xl px-4 py-4 md:px-6 md:py-6">
      {/* ✨ Hero card — the wow moment for first-time pitch */}
      <div
        className={`relative mb-5 overflow-hidden rounded-2xl border p-5 ${
          dental
            ? 'border-sky-200 bg-gradient-to-br from-sky-50 via-cyan-50 to-amber-50'
            : 'border-rose-200 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50'
        } [animation:hero-in_0.55s_cubic-bezier(0.22,1,0.36,1)_both]`}
      >
        <div
          className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-30 blur-3xl"
          style={{ background: dental ? '#22D3EE' : '#FB7185' }}
        />
        <div className="relative flex items-start gap-4">
          <div
            className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl ${dental ? 'bg-white/80 ring-1 ring-sky-200' : 'bg-white/80 ring-1 ring-rose-200'} shadow-[0_8px_28px_rgba(2,132,199,0.18)]`}
          >
            {dental ? (
              <ToothIcon className="h-10 w-10" animated />
            ) : (
              <HeartPulse
                size={32}
                className="text-rose-600 [animation:pulse-soft_2s_ease-in-out_infinite]"
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-display text-2xl font-semibold tracking-tight">
                {dental ? 'Your Dental Practice' : 'Your Practice'}
              </h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-emerald-300">
                <Sparkles size={10} />
                Botswana DPA-compliant
              </span>
            </div>
            <p className="text-muted mt-1 text-sm">
              {dental
                ? 'Charts, prescriptions, recall — every patient, every visit, on your phone.'
                : 'Patient records, visits, prescriptions — everything in one place.'}
            </p>

            {/* Animated stat row */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              <Stat
                label={dental ? 'Patients on chart' : 'Patients'}
                value={patients.length}
                tone={dental ? 'sky' : 'rose'}
              />
              <Stat
                label="Visits this month"
                value={visitsThisMonth}
                tone="amber"
                icon={<Activity size={12} />}
              />
              <Stat
                label="Consent on file"
                value={`${consentPct}%`}
                tone="emerald"
                icon={<ShieldCheck size={12} />}
              />
            </div>
          </div>
        </div>

        <div className="relative mt-5 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-600">
            🔒 Encrypted at rest · Audit logged on every record · 10-year retention
          </p>
          <Button
            size="sm"
            leftIcon={<Plus size={16} />}
            onClick={() => setShowAdd(true)}
            className="shadow-md"
          >
            {dental ? 'New patient' : 'New patient'}
          </Button>
        </div>
      </div>

      <Card compact className="mb-4 flex items-center gap-2">
        <Search size={16} className="text-muted" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            dental ? 'Search by name, Omang, or phone…' : 'Search by name, Omang, or phone…'
          }
          className="flex-1 bg-transparent text-sm outline-none"
        />
        <span className="text-muted text-xs">
          {filtered.length} of {patients.length}
        </span>
      </Card>

      {loading ? (
        <p className="text-muted text-sm">Loading…</p>
      ) : filtered.length === 0 ? (
        <Card className="py-12 text-center [animation:hero-in_0.5s_cubic-bezier(0.22,1,0.36,1)_both]">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center">
            {dental ? (
              <ToothIcon
                className="h-20 w-20 [animation:gentle-float_3.5s_ease-in-out_infinite]"
                animated
              />
            ) : (
              <Stethoscope
                size={48}
                className="text-muted opacity-60 [animation:gentle-float_3.5s_ease-in-out_infinite]"
              />
            )}
          </div>
          <h3 className="font-display mb-1 text-lg">
            {patients.length === 0
              ? dental
                ? 'Your chart is empty'
                : 'No patients yet'
              : 'No patients match that search'}
          </h3>
          <p className="text-muted mx-auto mb-5 max-w-sm text-sm">
            {patients.length === 0
              ? dental
                ? 'Register your first patient and Nako Pula will track every visit, prescription, recall, and procedure for them — automatically linked to your books.'
                : 'Register a patient and start logging visits, prescriptions, and follow-ups.'
              : 'Try a different name, Omang, or phone number.'}
          </p>
          {patients.length === 0 && (
            <Button onClick={() => setShowAdd(true)} leftIcon={<Plus size={16} />}>
              Register your first patient
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((p, i) => (
            <button
              key={p.id}
              onClick={async () => {
                await audit('view_patient', p.id);
                setSelectedId(p.id);
              }}
              className="block w-full text-left [animation:row-in_0.4s_cubic-bezier(0.22,1,0.36,1)_both]"
              style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}
            >
              <Card
                compact
                className="group transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${dental ? 'bg-sky-100 text-sky-700' : 'bg-rose-100 text-rose-700'} shadow-sm ring-2 ring-white transition-transform group-hover:scale-105`}
                    >
                      {(p.first_name[0] ?? '').toUpperCase()}
                      {(p.last_name[0] ?? '').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {p.first_name} {p.last_name}
                      </p>
                      <p className="text-muted text-xs">
                        {p.national_id ? `Omang ${p.national_id}` : '—'}
                        {p.phone ? ` · ${p.phone}` : ''}
                        {p.medical_aid_scheme ? ` · ${p.medical_aid_scheme}` : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {p.consent_to_store_records ? (
                      <Pill tone="green">
                        <ShieldCheck size={10} className="mr-1 inline" />
                        Consented
                      </Pill>
                    ) : (
                      <Pill tone="red">
                        <AlertTriangle size={10} className="mr-1 inline" />
                        No consent
                      </Pill>
                    )}
                    <p className="text-muted mt-1 text-xs">
                      {p.last_visit_at
                        ? `Last visit ${p.last_visit_at.slice(0, 10)}`
                        : 'No visits yet'}
                    </p>
                  </div>
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}

      {/* Page-local keyframes for entrance / hover animations */}
      <style>{`
        @keyframes hero-in {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes row-in {
          from { opacity: 0; transform: translateY(6px) scale(0.99); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes gentle-float {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(-4px); }
        }
        @keyframes pulse-soft {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.08); }
        }
      `}</style>

      <NewPatientModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        businessId={business.id}
        actorId={profile?.id ?? null}
        onCreated={async (id) => {
          await audit('create_patient', id);
          setShowAdd(false);
          void load();
        }}
      />
    </main>
  );
}

function Stat({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: number | string;
  tone: 'sky' | 'rose' | 'amber' | 'emerald';
  icon?: React.ReactNode;
}) {
  const toneClasses = {
    sky: 'from-sky-50 to-sky-100 ring-sky-200 text-sky-900',
    rose: 'from-rose-50 to-rose-100 ring-rose-200 text-rose-900',
    amber: 'from-amber-50 to-amber-100 ring-amber-200 text-amber-900',
    emerald: 'from-emerald-50 to-emerald-100 ring-emerald-200 text-emerald-900',
  };
  return (
    <div
      className={`rounded-xl bg-gradient-to-br ${toneClasses[tone]} px-3 py-2 ring-1 [animation:row-in_0.5s_cubic-bezier(0.22,1,0.36,1)_both]`}
    >
      <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {icon}
        {label}
      </p>
      <p className="font-display mt-0.5 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}

// ───── New-patient form ─────────────────────────────────────────────

function NewPatientModal({
  open,
  onClose,
  businessId,
  actorId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  actorId: string | null;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const regionConfig = useRegionConfig();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '' as '' | Gender,
    national_id: '',
    phone: '',
    email: '',
    address: '',
    next_of_kin_name: '',
    next_of_kin_phone: '',
    next_of_kin_relationship: '',
    medical_aid_scheme: '',
    medical_aid_member_number: '',
    blood_group: '',
    known_allergies: '',
    chronic_conditions: '',
    consent: false,
  });

  const reset = () =>
    setForm({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      national_id: '',
      phone: '',
      email: '',
      address: '',
      next_of_kin_name: '',
      next_of_kin_phone: '',
      next_of_kin_relationship: '',
      medical_aid_scheme: '',
      medical_aid_member_number: '',
      blood_group: '',
      known_allergies: '',
      chronic_conditions: '',
      consent: false,
    });

  const create = async () => {
    if (!form.first_name || !form.last_name) {
      toast.show('First and last name are required.', { tone: 'warning' });
      return;
    }
    if (!form.consent) {
      toast.show(
        `Patient must consent to record storage (${regionConfig.code === 'ZA' ? 'POPIA' : regionConfig.code === 'NG' ? 'NDPR' : regionConfig.code === 'ZW' ? 'Zimbabwe DPA' : 'Botswana DPA'} — special category data).`,
        {
          tone: 'warning',
          durationMs: 5000,
        },
      );
      return;
    }
    setBusy(true);
    const { data, error } = await supabase
      .from('patients')
      .insert({
        business_id: businessId,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        date_of_birth: form.date_of_birth || null,
        gender: form.gender || null,
        national_id: form.national_id || null,
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        next_of_kin_name: form.next_of_kin_name || null,
        next_of_kin_phone: form.next_of_kin_phone || null,
        next_of_kin_relationship: form.next_of_kin_relationship || null,
        medical_aid_scheme: form.medical_aid_scheme || null,
        medical_aid_member_number: form.medical_aid_member_number || null,
        blood_group: form.blood_group || null,
        known_allergies: form.known_allergies || null,
        chronic_conditions: form.chronic_conditions || null,
        consent_to_store_records: form.consent,
        consent_recorded_at: form.consent ? new Date().toISOString() : null,
        created_by: actorId,
      })
      .select('id')
      .single();
    setBusy(false);
    if (error) {
      toast.show(error.message, { tone: 'error' });
      return;
    }
    toast.show('Patient registered.', { tone: 'success' });
    reset();
    onCreated((data as { id: string }).id);
  };

  return (
    <Modal open={open} onClose={onClose} title="Register new patient">
      <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Identity</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="First name"
            value={form.first_name}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            required
          />
          <Input
            label="Last name"
            value={form.last_name}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="date"
            label="Date of birth"
            value={form.date_of_birth}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Gender</label>
            <select
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value as '' | Gender })}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </div>
        </div>
        <Input
          label="Omang (national ID)"
          value={form.national_id}
          onChange={(e) => setForm({ ...form, national_id: e.target.value })}
          hint="Used to recognise the same patient across visits"
        />

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Contact</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
          <Input
            type="email"
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </div>
        <Input
          label="Address"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Next of kin</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Name"
            value={form.next_of_kin_name}
            onChange={(e) => setForm({ ...form, next_of_kin_name: e.target.value })}
          />
          <Input
            label="Phone"
            value={form.next_of_kin_phone}
            onChange={(e) => setForm({ ...form, next_of_kin_phone: e.target.value })}
          />
        </div>
        <Input
          label="Relationship"
          value={form.next_of_kin_relationship}
          onChange={(e) => setForm({ ...form, next_of_kin_relationship: e.target.value })}
        />

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Medical aid</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Scheme"
            value={form.medical_aid_scheme}
            onChange={(e) => setForm({ ...form, medical_aid_scheme: e.target.value })}
            hint="BPOMAS, BOMaid, Pula Medical, etc."
          />
          <Input
            label="Member number"
            value={form.medical_aid_member_number}
            onChange={(e) => setForm({ ...form, medical_aid_member_number: e.target.value })}
          />
        </div>

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Clinical</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Blood group</label>
            <select
              value={form.blood_group}
              onChange={(e) => setForm({ ...form, blood_group: e.target.value })}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              <option value="">—</option>
              {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((bg) => (
                <option key={bg}>{bg}</option>
              ))}
            </select>
          </div>
        </div>
        <Input
          label="Known allergies"
          value={form.known_allergies}
          onChange={(e) => setForm({ ...form, known_allergies: e.target.value })}
          placeholder="penicillin, peanuts, …"
        />
        <Input
          label="Chronic conditions"
          value={form.chronic_conditions}
          onChange={(e) => setForm({ ...form, chronic_conditions: e.target.value })}
          placeholder="hypertension, diabetes, …"
        />

        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={form.consent}
              onChange={(e) => setForm({ ...form, consent: e.target.checked })}
              className="mt-0.5"
            />
            <span>
              <strong>Patient consent.</strong> The patient agrees to store and process their health
              records on this system in line with the{' '}
              {regionConfig.code === 'ZA'
                ? 'Protection of Personal Information Act (POPIA)'
                : regionConfig.code === 'NG'
                  ? 'Nigeria Data Protection Regulation (NDPR)'
                  : regionConfig.code === 'ZW'
                    ? 'Zimbabwe Data Protection Act'
                    : 'Botswana Data Protection Act 2018'}
              . Records are kept for 10 years from the last consultation.
            </span>
          </label>
        </div>

        <Button full onClick={create} loading={busy} leftIcon={<Save size={16} />}>
          Register patient
        </Button>
      </div>
    </Modal>
  );
}

// ───── Patient detail + visits ──────────────────────────────────────

function PatientDetail({
  patientId,
  businessId,
  actorId,
  attendedByDefault,
  dental,
  onBack,
}: {
  patientId: string;
  businessId: string;
  actorId: string | null;
  attendedByDefault: string | null;
  dental: boolean;
  onBack: () => void;
}) {
  const [patient, setPatient] = useState<Patient | null>(null);
  const [visits, setVisits] = useState<PatientVisit[]>([]);
  const [showNewVisit, setShowNewVisit] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const [p, v] = await Promise.all([
        supabase.from('patients').select('*').eq('id', patientId).single(),
        supabase
          .from('patient_visits')
          .select('*')
          .eq('patient_id', patientId)
          .is('deleted_at', null)
          .order('visit_date', { ascending: false }),
      ]);
      setPatient((p.data as Patient) ?? null);
      setVisits((v.data ?? []) as PatientVisit[]);
      setLoading(false);
    })();
  }, [patientId]);

  if (loading || !patient) {
    return (
      <main className="mx-auto max-w-3xl px-4 py-6 text-sm">
        <button onClick={onBack} className="text-muted mb-4 inline-flex items-center gap-1 text-sm">
          <ChevronLeft size={14} /> Back to patients
        </button>
        <p className="text-muted">{loading ? 'Loading…' : 'Patient not found.'}</p>
      </main>
    );
  }

  const age = patient.date_of_birth
    ? Math.floor((Date.now() - new Date(patient.date_of_birth).getTime()) / (365.25 * 86_400_000))
    : null;

  return (
    <main className="mx-auto max-w-3xl px-4 py-4 md:px-6 md:py-6">
      <button onClick={onBack} className="text-muted mb-3 inline-flex items-center gap-1 text-sm">
        <ChevronLeft size={14} /> Back to patients
      </button>

      <Card className="mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-xl">
              {patient.first_name} {patient.last_name}
            </h1>
            <p className="text-muted text-sm">
              {age != null ? `${age} years` : '—'}
              {patient.gender ? ` · ${patient.gender}` : ''}
              {patient.blood_group ? ` · Blood ${patient.blood_group}` : ''}
            </p>
            <p className="text-muted mt-1 text-xs">
              {patient.national_id ? `Omang ${patient.national_id}` : ''}
              {patient.phone ? ` · ${patient.phone}` : ''}
            </p>
          </div>
          {patient.consent_to_store_records ? (
            <Pill tone="green">
              <ShieldCheck size={10} className="mr-1 inline" /> Consented
            </Pill>
          ) : (
            <Pill tone="red">
              <AlertTriangle size={10} className="mr-1 inline" /> No consent
            </Pill>
          )}
        </div>

        {(patient.known_allergies || patient.chronic_conditions) && (
          <div className="border-line mt-3 border-t pt-3 text-sm">
            {patient.known_allergies && (
              <p>
                <strong className="text-red">Allergies:</strong> {patient.known_allergies}
              </p>
            )}
            {patient.chronic_conditions && (
              <p>
                <strong>Chronic:</strong> {patient.chronic_conditions}
              </p>
            )}
          </div>
        )}

        {patient.medical_aid_scheme && (
          <p className="text-muted mt-2 text-xs">
            Medical aid: <strong>{patient.medical_aid_scheme}</strong>{' '}
            {patient.medical_aid_member_number && `(#${patient.medical_aid_member_number})`}
          </p>
        )}
      </Card>

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-display flex items-center gap-2 text-lg">
          <Calendar size={18} /> Visits ({visits.length})
        </h2>
        <Button size="sm" leftIcon={<Plus size={16} />} onClick={() => setShowNewVisit(true)}>
          New visit
        </Button>
      </div>

      {visits.length === 0 ? (
        <Card className="py-8 text-center">
          <p className="text-muted text-sm">No visits recorded yet.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {visits.map((v) => (
            <VisitRow key={v.id} visit={v} />
          ))}
        </div>
      )}

      <NewVisitModal
        open={showNewVisit}
        onClose={() => setShowNewVisit(false)}
        businessId={businessId}
        patientId={patient.id}
        attendedBy={attendedByDefault}
        dental={dental}
        onCreated={async (visitId) => {
          await supabase.from('patient_audit_log').insert({
            business_id: businessId,
            patient_id: patient.id,
            visit_id: visitId,
            actor_id: actorId,
            action: 'create_visit' as PatientAuditAction,
            context: {},
          });
          await supabase
            .from('patients')
            .update({ last_visit_at: new Date().toISOString() })
            .eq('id', patient.id);
          setShowNewVisit(false);
          // Refresh
          const v = await supabase
            .from('patient_visits')
            .select('*')
            .eq('patient_id', patient.id)
            .is('deleted_at', null)
            .order('visit_date', { ascending: false });
          setVisits((v.data ?? []) as PatientVisit[]);
        }}
      />
    </main>
  );
}

function VisitRow({ visit }: { visit: PatientVisit }) {
  const regionCode = useRegionStore((s) => s.code);
  const [open, setOpen] = useState(false);
  const [rx, setRx] = useState<PatientPrescription[] | null>(null);

  const toggle = async () => {
    if (!open && !rx) {
      const { data } = await supabase
        .from('patient_prescriptions')
        .select('*')
        .eq('visit_id', visit.id);
      setRx((data ?? []) as PatientPrescription[]);
    }
    setOpen(!open);
  };

  return (
    <Card compact>
      <button onClick={toggle} className="block w-full text-left">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Pill tone="blue">{visit.visit_type.replace('_', ' ')}</Pill>
              <span className="text-sm font-medium">{visit.visit_date}</span>
              {visit.is_locked && <Lock size={12} className="text-muted" />}
            </div>
            <p className="text-muted mt-1 text-xs">
              {visit.chief_complaint ?? visit.diagnosis ?? '—'}
            </p>
          </div>
          <div className="text-right">
            <p className="tnum font-mono text-sm">
              {formatMoney(visit.amount_charged, regionCode)}
            </p>
            <p className="text-muted text-[10px] uppercase">{visit.payment_status}</p>
          </div>
        </div>
      </button>

      {open && (
        <div className="border-line mt-3 space-y-2 border-t pt-3 text-sm">
          {visit.examination && (
            <p>
              <strong>Examination:</strong> {visit.examination}
            </p>
          )}
          {visit.diagnosis && (
            <p>
              <strong>Diagnosis:</strong> {visit.diagnosis}
            </p>
          )}
          {visit.treatment_notes && (
            <p>
              <strong>Treatment:</strong> {visit.treatment_notes}
            </p>
          )}
          {visit.vitals && Object.keys(visit.vitals).length > 0 && (
            <p className="text-muted text-xs">
              {visit.vitals.bp_systolic && visit.vitals.bp_diastolic
                ? `BP ${visit.vitals.bp_systolic}/${visit.vitals.bp_diastolic} · `
                : ''}
              {visit.vitals.pulse ? `Pulse ${visit.vitals.pulse} · ` : ''}
              {visit.vitals.temp_c ? `Temp ${visit.vitals.temp_c}°C · ` : ''}
              {visit.vitals.weight_kg ? `Weight ${visit.vitals.weight_kg}kg` : ''}
            </p>
          )}
          {rx && rx.length > 0 && (
            <div>
              <p className="text-muted text-xs font-semibold uppercase tracking-wider">
                <PillIcon size={12} className="mr-1 inline" /> Prescriptions
              </p>
              <ul className="mt-1 space-y-1">
                {rx.map((p) => (
                  <li key={p.id} className="text-xs">
                    <strong>{p.drug_name}</strong>
                    {p.strength ? ` ${p.strength}` : ''}
                    {p.dosage ? ` — ${p.dosage}` : ''}
                    {p.frequency ? ` ${p.frequency}` : ''}
                    {p.duration ? ` × ${p.duration}` : ''}
                    {p.instructions ? `. ${p.instructions}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ───── New-visit form ───────────────────────────────────────────────

function NewVisitModal({
  open,
  onClose,
  businessId,
  patientId,
  attendedBy,
  dental,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string;
  patientId: string;
  attendedBy: string | null;
  dental: boolean;
  onCreated: (visitId: string) => void;
}) {
  const toast = useToast();
  const regionConfig = useRegionConfig();
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  const [visitType, setVisitType] = useState<VisitType>('consultation');
  const [visitDate, setVisitDate] = useState(today);
  const [complaint, setComplaint] = useState('');
  const [bp, setBp] = useState('');
  const [pulse, setPulse] = useState('');
  const [temp, setTemp] = useState('');
  const [weight, setWeight] = useState('');
  const [examination, setExamination] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [treatment, setTreatment] = useState('');
  const [followUp, setFollowUp] = useState('');
  const [amount, setAmount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('unpaid');

  type RxDraft = {
    drug_name: string;
    strength: string;
    dosage: string;
    frequency: string;
    duration: string;
    instructions: string;
  };
  const blankRx: RxDraft = {
    drug_name: '',
    strength: '',
    dosage: '',
    frequency: '',
    duration: '',
    instructions: '',
  };
  const [rx, setRx] = useState<RxDraft[]>([{ ...blankRx }]);

  // Dental-only: which teeth are affected this visit (FDI numbering).
  const [affectedTeeth, setAffectedTeeth] = useState<string[]>([]);

  const reset = () => {
    setVisitType('consultation');
    setVisitDate(today);
    setComplaint('');
    setBp('');
    setPulse('');
    setTemp('');
    setWeight('');
    setExamination('');
    setDiagnosis('');
    setTreatment('');
    setFollowUp('');
    setAmount(0);
    setPaymentStatus('unpaid');
    setRx([{ ...blankRx }]);
    setAffectedTeeth([]);
  };

  const save = async () => {
    setBusy(true);

    const vitals: PatientVitals = {};
    const bpMatch = bp.match(/^\s*(\d+)\s*\/\s*(\d+)\s*$/);
    if (bpMatch) {
      vitals.bp_systolic = Number(bpMatch[1]);
      vitals.bp_diastolic = Number(bpMatch[2]);
    }
    if (pulse) vitals.pulse = Number(pulse);
    if (temp) vitals.temp_c = Number(temp);
    if (weight) vitals.weight_kg = Number(weight);

    // For dentists, prepend the affected-teeth list to the examination notes
    // so the FDI codes ride along with the rest of the clinical record.
    const examinationFinal =
      dental && affectedTeeth.length > 0
        ? `Teeth: ${[...affectedTeeth].sort().join(', ')}${examination ? `\n${examination}` : ''}`
        : examination;

    const { data: visit, error } = await supabase
      .from('patient_visits')
      .insert({
        business_id: businessId,
        patient_id: patientId,
        visit_date: visitDate,
        visit_type: visitType,
        chief_complaint: complaint || null,
        vitals,
        examination: examinationFinal || null,
        diagnosis: diagnosis || null,
        treatment_notes: treatment || null,
        follow_up_at: followUp || null,
        amount_charged: amount,
        payment_status: paymentStatus,
        attended_by: attendedBy,
      })
      .select('id')
      .single();

    if (error || !visit) {
      setBusy(false);
      toast.show(error?.message ?? 'Could not save visit.', { tone: 'error' });
      return;
    }
    const visitId = (visit as { id: string }).id;

    const realRx = rx
      .filter((r) => r.drug_name.trim())
      .map((r) => ({
        business_id: businessId,
        visit_id: visitId,
        patient_id: patientId,
        drug_name: r.drug_name.trim(),
        strength: r.strength || null,
        dosage: r.dosage || null,
        frequency: r.frequency || null,
        duration: r.duration || null,
        instructions: r.instructions || null,
      }));
    if (realRx.length > 0) {
      const { error: rxErr } = await supabase.from('patient_prescriptions').insert(realRx);
      if (rxErr) {
        toast.show(`Visit saved, but prescriptions failed: ${rxErr.message}`, { tone: 'warning' });
      }
    }

    setBusy(false);
    toast.show('Visit recorded.', { tone: 'success' });
    playChime('success');
    reset();
    onCreated(visitId);
  };

  return (
    <Modal open={open} onClose={onClose} title="New visit">
      <div className="max-h-[75vh] space-y-3 overflow-y-auto pr-1">
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Visit type</label>
            <select
              value={visitType}
              onChange={(e) => setVisitType(e.target.value as VisitType)}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              <option value="consultation">Consultation</option>
              <option value="follow_up">Follow-up</option>
              <option value="procedure">Procedure</option>
              <option value="prescription">Prescription</option>
              <option value="dispense">Dispense</option>
              <option value="lab_request">Lab request</option>
              <option value="referral">Referral</option>
              <option value="tele_consult">Tele-consult</option>
            </select>
          </div>
          <Input
            type="date"
            label="Visit date"
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value)}
          />
        </div>

        <Input
          label="Chief complaint"
          value={complaint}
          onChange={(e) => setComplaint(e.target.value)}
          placeholder="What brought the patient in?"
        />

        {dental && (
          <>
            <p className="text-muted text-xs font-semibold uppercase tracking-wider">
              Dental chart
            </p>
            <ToothChart selected={affectedTeeth} onChange={setAffectedTeeth} />
          </>
        )}

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Vitals</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="BP (e.g. 120/80)"
            value={bp}
            onChange={(e) => setBp(e.target.value)}
            placeholder="120/80"
          />
          <Input
            label="Pulse"
            type="number"
            numeric
            value={pulse || ''}
            onChange={(e) => setPulse(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Temp (°C)"
            type="number"
            numeric
            value={temp || ''}
            onChange={(e) => setTemp(e.target.value)}
          />
          <Input
            label="Weight (kg)"
            type="number"
            numeric
            value={weight || ''}
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>

        <Input
          label="Examination findings"
          value={examination}
          onChange={(e) => setExamination(e.target.value)}
        />
        <Input label="Diagnosis" value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
        <Input
          label="Treatment / notes"
          value={treatment}
          onChange={(e) => setTreatment(e.target.value)}
        />
        <Input
          type="date"
          label="Follow-up on"
          value={followUp}
          onChange={(e) => setFollowUp(e.target.value)}
        />

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">Billing</p>
        <div className="grid grid-cols-2 gap-3">
          <Input
            type="number"
            numeric
            leftAddon={regionConfig.currencySymbol}
            label="Amount charged"
            value={amount || ''}
            onChange={(e) => setAmount(Number(e.target.value))}
          />
          <div className="flex flex-col gap-1">
            <label className="text-sm font-medium">Payment</label>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
              className="min-h-tap rounded-btn border-line border bg-white px-3"
            >
              <option value="unpaid">Unpaid</option>
              <option value="paid">Paid (cash)</option>
              <option value="medical_aid">Medical aid</option>
              <option value="waived">Waived</option>
            </select>
          </div>
        </div>

        <p className="text-muted text-xs font-semibold uppercase tracking-wider">
          <PillIcon size={12} className="mr-1 inline" /> Prescriptions
        </p>
        {rx.map((r, i) => (
          <div key={i} className="border-line space-y-2 rounded-md border p-3">
            <Input
              label={`Drug name ${i + 1}`}
              value={r.drug_name}
              onChange={(e) => {
                const copy = [...rx];
                copy[i] = { ...r, drug_name: e.target.value };
                setRx(copy);
              }}
              placeholder="Amoxicillin"
            />
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Strength"
                value={r.strength}
                onChange={(e) => {
                  const copy = [...rx];
                  copy[i] = { ...r, strength: e.target.value };
                  setRx(copy);
                }}
                placeholder="500mg"
              />
              <Input
                label="Dosage"
                value={r.dosage}
                onChange={(e) => {
                  const copy = [...rx];
                  copy[i] = { ...r, dosage: e.target.value };
                  setRx(copy);
                }}
                placeholder="1 tab"
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Frequency"
                value={r.frequency}
                onChange={(e) => {
                  const copy = [...rx];
                  copy[i] = { ...r, frequency: e.target.value };
                  setRx(copy);
                }}
                placeholder="TDS"
              />
              <Input
                label="Duration"
                value={r.duration}
                onChange={(e) => {
                  const copy = [...rx];
                  copy[i] = { ...r, duration: e.target.value };
                  setRx(copy);
                }}
                placeholder="7 days"
              />
            </div>
            <Input
              label="Instructions"
              value={r.instructions}
              onChange={(e) => {
                const copy = [...rx];
                copy[i] = { ...r, instructions: e.target.value };
                setRx(copy);
              }}
              placeholder="After meals"
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => setRx([...rx, { ...blankRx }])}
          className="text-accent text-xs underline"
        >
          + Add another medicine
        </button>

        <Button full onClick={save} loading={busy} leftIcon={<Save size={16} />}>
          Save visit
        </Button>
      </div>
    </Modal>
  );
}
